import { useState, useEffect, useRef, useCallback } from 'react';
import { BRRApiClient } from '../services/api';

interface MaterialResult {
  materialId: string;
  productName: string | null;
}

interface UseMaterialSearchOptions {
  apiClient: BRRApiClient;
  query: string;
  debounceMs?: number;
  minQueryLength?: number;
}

interface UseMaterialSearchReturn {
  results: MaterialResult[];
  loading: boolean;
  error: Error | null;
}

export const useMaterialSearch = ({
  apiClient,
  query,
  debounceMs = 400,
  minQueryLength = 3,
}: UseMaterialSearchOptions): UseMaterialSearchReturn => {
  const [results, setResults] = useState<MaterialResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const timeoutRef = useRef<number | null>(null);

  const search = useCallback(async (searchQuery: string) => {
    if (searchQuery.length < minQueryLength) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.searchMaterials(searchQuery);
      setResults(response.results);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [apiClient, minQueryLength]);

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (query.length < minQueryLength) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    timeoutRef.current = window.setTimeout(() => {
      void search(query);
    }, debounceMs);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [query, debounceMs, minQueryLength, search]);

  return { results, loading, error };
};
